import React from 'react';
import { FileText, UsersThree, Link as LinkIcon, PaperPlaneTilt, Warning } from '@phosphor-icons/react';

const levelBadgeColor = (level) => {
  const l = (level || '').toLowerCase();
  if (l.includes('federal') || l.includes('national')) return 'bg-blue-50 text-blue-700 border-blue-200';
  if (l.includes('state') || l.includes('provinc')) return 'bg-purple-50 text-purple-700 border-purple-200';
  if (l.includes('county')) return 'bg-teal-50 text-teal-700 border-teal-200';
  if (l.includes('local') || l.includes('city') || l.includes('municipal')) return 'bg-amber-50 text-amber-700 border-amber-200';
  if (l.includes('international')) return 'bg-green-50 text-green-700 border-green-200';
  return 'bg-gray-50 text-gray-700 border-gray-200';
};

// Ordered groups: high-level jurisdiction tiers, federal first down to local.
const LEVEL_GROUPS = [
  { label: 'Federal / National', match: (l) => l.includes('federal') || l.includes('national') },
  { label: 'State / Provincial', match: (l) => l.includes('state') || l.includes('provinc') || l.includes('province') },
  { label: 'County', match: (l) => l.includes('county') },
  { label: 'Local / Municipal', match: (l) => l.includes('local') || l.includes('municipal') || l.includes('city') || l.includes('by-law') || l.includes('bylaw') || l.includes('ordinance') },
  { label: 'International', match: (l) => l.includes('international') },
];

const groupLaws = (laws) => {
  const groups = LEVEL_GROUPS.map(g => ({ label: g.label, laws: [] }));
  const other = { label: 'Other', laws: [] };
  laws.forEach((law) => {
    const lvl = (law.level || '').toLowerCase();
    const idx = LEVEL_GROUPS.findIndex(g => g.match(lvl));
    if (idx >= 0) groups[idx].laws.push(law);
    else other.laws.push(law);
  });
  return [...groups, other].filter(g => g.laws.length > 0);
};

const LawCard = ({ law, idx }) => (
  <div className="p-3 border border-[#E5E7EB] rounded-md bg-white" data-testid={`legal-panel-law-${idx}`}>
    <div className="flex items-start justify-between gap-2 flex-wrap">
      <div className="flex-1 min-w-0">
        <p className="font-ibm font-semibold text-sm text-[#09090B]">{law.title}</p>
        {law.high_level && (
          <p className="text-xs text-[#6B7280] font-ibm mt-0.5"><span className="font-semibold text-[#09090B]">Framework:</span> {law.high_level}</p>
        )}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {law.category && (
          <span className="text-[10px] font-ibm font-bold uppercase tracking-wider px-2 py-0.5 rounded border bg-[#F3F4F6] text-[#09090B] border-[#E5E7EB]">{law.category}</span>
        )}
        {law.level && (
          <span className={`text-[10px] font-ibm font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${levelBadgeColor(law.level)}`}>{law.level}</span>
        )}
      </div>
    </div>

    {law.summary && <p className="text-xs text-[#6B7280] font-ibm mt-2">{law.summary}</p>}

    {(law.specific_provisions || []).length > 0 && (
      <div className="mt-2">
        <p className="text-[11px] font-bold uppercase tracking-wider text-[#002FA7] font-ibm mb-1">Specific Provisions</p>
        <ul className="space-y-0.5">
          {law.specific_provisions.map((p, i) => (
            <li key={`prov-${i}`} className="text-xs font-ibm text-[#09090B] flex gap-1.5"><span className="text-[#002FA7]">§</span><span>{p}</span></li>
          ))}
        </ul>
      </div>
    )}

    {law.citation && <p className="text-[11px] font-mono text-[#6B7280] mt-2">Citation: {law.citation}</p>}
    {law.how_it_applies && <p className="text-xs text-[#09090B] font-ibm mt-2"><span className="font-semibold">How it applies:</span> {law.how_it_applies}</p>}

    {law.official_url && (
      <a href={law.official_url} target="_blank" rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-xs text-[#002FA7] font-ibm font-semibold mt-2 hover:underline"
        data-testid={`legal-panel-law-link-${idx}`}>
        <LinkIcon size={12} /> {law.source_name || 'Official source'}
      </a>
    )}
  </div>
);

export const LegalPanelResults = ({ panel, generating }) => {
  if (generating) {
    return (
      <div className="mt-4 p-4 border border-dashed border-[#E5E7EB] rounded-md text-center">
        <span className="text-sm text-[#6B7280] font-ibm">Researching official statutes, ordinances and agencies across federal, state and local levels. This may take 15–30 seconds…</span>
      </div>
    );
  }

  if (!panel) {
    return (
      <div className="mt-3 p-4 border border-dashed border-[#E5E7EB] rounded-md text-center">
        <p className="text-sm text-[#6B7280] font-ibm">Enter the jurisdiction and incident type above (or let AI suggest), then click <span className="font-semibold text-[#09090B]">Generate Panel</span> to get applicable laws and agencies.</p>
      </div>
    );
  }

  const laws = panel.laws || [];
  const lawGroups = groupLaws(laws);
  let lawIdx = 0;

  return (
    <div className="mt-4 space-y-4" data-testid="legal-panel-results">
      {panel.jurisdiction_summary && (
        <div className="p-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-md">
          <p className="text-sm font-ibm text-[#09090B]">{panel.jurisdiction_summary}</p>
          <p className="text-xs text-[#6B7280] font-ibm mt-1">
            Jurisdiction: {[panel.jurisdiction?.city, panel.jurisdiction?.state, panel.jurisdiction?.country].filter(Boolean).join(', ') || '—'}
            {panel.incident_type ? ` • ${panel.incident_type}` : ''}
          </p>
        </div>
      )}

      {/* Laws grouped by level (federal → state → local) */}
      <div data-testid="legal-panel-laws">
        <h4 className="font-outfit text-base font-semibold text-[#09090B] mb-2 flex items-center gap-2">
          <FileText size={16} className="text-[#002FA7]" /> Applicable Laws ({laws.length})
        </h4>
        {laws.length === 0 ? (
          <p className="text-xs text-[#6B7280] font-ibm italic">No verified laws returned. Try refining the jurisdiction or incident type.</p>
        ) : (
          <div className="space-y-4">
            {lawGroups.map((group) => (
              <div key={group.label} data-testid={`legal-panel-group-${group.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                <p className="text-xs font-bold tracking-[0.12em] uppercase text-[#6B7280] font-ibm mb-1.5 border-b border-[#E5E7EB] pb-1">{group.label} <span className="text-[#9CA3AF]">({group.laws.length})</span></p>
                <div className="space-y-2">
                  {group.laws.map((law) => <LawCard key={`law-${lawIdx}`} law={law} idx={lawIdx++} />)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Agencies */}
      <div data-testid="legal-panel-agencies">
        <h4 className="font-outfit text-base font-semibold text-[#09090B] mb-2 flex items-center gap-2">
          <UsersThree size={16} className="text-[#002FA7]" /> Government Agencies ({(panel.agencies || []).length})
        </h4>
        {(panel.agencies || []).length === 0 ? (
          <p className="text-xs text-[#6B7280] font-ibm italic">No verified agencies returned. Try refining the jurisdiction or incident type.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {panel.agencies.map((ag, i) => (
              <div key={`${ag.name || 'agency'}-${i}`} className="p-3 border border-[#E5E7EB] rounded-md bg-white" data-testid={`legal-panel-agency-${i}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <p className="font-ibm font-semibold text-sm text-[#09090B] flex-1 min-w-0">{ag.name}</p>
                  {ag.level && (
                    <span className={`text-[10px] font-ibm font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${levelBadgeColor(ag.level)}`}>{ag.level}</span>
                  )}
                </div>
                {ag.role && <p className="text-xs text-[#6B7280] font-ibm mt-1">{ag.role}</p>}
                <div className="mt-2 space-y-0.5 text-xs font-ibm">
                  {ag.website && (
                    <a href={ag.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#002FA7] hover:underline">
                      <LinkIcon size={11} /> Website
                    </a>
                  )}
                  {ag.complaint_url && (
                    <div>
                      <a href={ag.complaint_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#FF9500] hover:underline">
                        <PaperPlaneTilt size={11} /> File a complaint
                      </a>
                    </div>
                  )}
                  {ag.phone && <div className="text-[#09090B]">Phone: {ag.phone}</div>}
                  {ag.email && (
                    <div className="text-[#09090B] truncate">Email: <a href={`mailto:${ag.email}`} className="hover:underline">{ag.email}</a></div>
                  )}
                  {ag.address && <div className="text-[#6B7280] text-[11px] mt-1">{ag.address}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {panel.disclaimer && (
        <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-md">
          <p className="text-[11px] text-amber-900 font-ibm flex items-start gap-1.5">
            <Warning size={12} className="mt-0.5 shrink-0" /> {panel.disclaimer}
          </p>
        </div>
      )}
      {panel.generated_at && (
        <p className="text-[11px] text-[#9CA3AF] font-ibm">Generated {new Date(panel.generated_at).toLocaleString()}</p>
      )}
    </div>
  );
};
